import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Zap,
  ArrowLeftRight,
  FileText,
  CheckCircle,
  Clock,
  AlertCircle,
  TrendingUp,
  Upload,
  Sparkles,
} from "lucide-react";
import { v1GetStats } from "../api";
import "./V1Dashboard.css";

const V1Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Get user role from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdminOrSuperadmin = user?.role === 'admin' || user?.role === 'superadmin';

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const data = await v1GetStats();
      setStats(data);
    } catch (err) {
      console.error("Failed to fetch V1 stats:", err);
    } finally {
      setLoading(false);
    }
  };

  const modelCards = [
    {
      id: "xorbit",
      name: "XOrbit",
      description: "Gemini 3.5 Flash",
      icon: Zap,
      color: "#8b5cf6",
      bgColor: "#f3e8ff",
      path: "/v1/xorbit",
      stats: stats?.xorbit_work_orders || 0,
    },
    {
      id: "vja",
      name: "VJA",
      description: "Qwen VL",
      icon: Zap,
      color: "#0ea5e9",
      bgColor: "#e0f2fe",
      path: "/v1/vja",
      stats: stats?.vja_work_orders || 0,
    },
    // V3 card - only visible to admin/superadmin
    ...(isAdminOrSuperadmin ? [{
      id: "v3",
      name: "XOrbit V3",
      description: "Preprocessing + Qwen",
      icon: Sparkles,
      color: "#10b981",
      bgColor: "#d1fae5",
      path: "/v1/v3-workorders",
      stats: stats?.v3_work_orders || 0,
    }] : []),
  ];

  return (
    <div className="v1-dashboard">
      {/* Welcome Banner */}
      <div className="v1-welcome-banner">
        <div className="v1-welcome-content">
          <h1>V1 Extraction Pipeline</h1>
          <p>Compare and analyze document extraction using XOrbit (Gemini) and VJA (Qwen) models</p>
        </div>
        <div className="v1-welcome-actions">
          <button className="v1-welcome-btn primary" onClick={() => navigate("/v1/comparison")}>
            <ArrowLeftRight size={20} />
            Compare Models
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="v1-stats-overview">
        <div className="v1-stat-card total">
          <div className="v1-stat-icon">
            <FileText size={24} />
          </div>
          <div className="v1-stat-info">
            <span className="v1-stat-value">{loading ? "-" : stats?.total_work_orders || 0}</span>
            <span className="v1-stat-label">Total Work Orders</span>
          </div>
        </div>

        <div className="v1-stat-card completed">
          <div className="v1-stat-icon">
            <CheckCircle size={24} />
          </div>
          <div className="v1-stat-info">
            <span className="v1-stat-value">{loading ? "-" : stats?.by_status?.completed || 0}</span>
            <span className="v1-stat-label">Completed</span>
          </div>
        </div>

        <div className="v1-stat-card processing">
          <div className="v1-stat-icon">
            <Clock size={24} />
          </div>
          <div className="v1-stat-info">
            <span className="v1-stat-value">{loading ? "-" : stats?.by_status?.processing || 0}</span>
            <span className="v1-stat-label">Processing</span>
          </div>
        </div>

        <div className="v1-stat-card failed">
          <div className="v1-stat-icon">
            <AlertCircle size={24} />
          </div>
          <div className="v1-stat-info">
            <span className="v1-stat-value">{loading ? "-" : stats?.by_status?.failed || 0}</span>
            <span className="v1-stat-label">Failed</span>
          </div>
        </div>
      </div>

      {/* Model Cards */}
      <h2 className="v1-section-title">
        <TrendingUp size={20} />
        Extraction Models
      </h2>

      <div className="v1-model-cards">
        {modelCards.map((model) => (
          <div
            key={model.id}
            className="v1-model-card"
            style={{ "--model-color": model.color, "--model-bg": model.bgColor }}
            onClick={() => navigate(model.path)}
          >
            <div className="v1-model-card-header">
              <div className="v1-model-icon" style={{ backgroundColor: model.bgColor }}>
                <model.icon size={28} style={{ color: model.color }} />
              </div>
              <div className="v1-model-info">
                <h3>{model.name}</h3>
                <p>{model.description}</p>
              </div>
            </div>

            <div className="v1-model-card-stats">
              <div className="v1-model-stat">
                <span className="v1-model-stat-value">{model.stats}</span>
                <span className="v1-model-stat-label">Work Orders</span>
              </div>
            </div>

            <div className="v1-model-card-action">
              <button>
                <Upload size={16} />
                Upload & Extract
              </button>
            </div>
          </div>
        ))}

        {/* Comparison Card */}
        <div
          className="v1-model-card comparison"
          onClick={() => navigate("/v1/comparison")}
        >
          <div className="v1-model-card-header">
            <div className="v1-model-icon comparison-icon">
              <ArrowLeftRight size={28} />
            </div>
            <div className="v1-model-info">
              <h3>Model Comparison</h3>
              <p>Compare XOrbit vs VJA</p>
            </div>
          </div>

          <div className="v1-model-card-stats">
            <div className="v1-model-stat">
              <span className="v1-model-stat-value">{stats?.total_comparisons || 0}</span>
              <span className="v1-model-stat-label">Comparisons</span>
            </div>
          </div>

          <div className="v1-model-card-action">
            <button>
              <ArrowLeftRight size={16} />
              Start Comparison
            </button>
          </div>
        </div>
      </div>

      {/* Quick Info */}
      <div className="v1-quick-info">
        <div className="v1-info-card">
          <h4>About XOrbit</h4>
          <p>Uses Google's Gemini 3.5 Flash model for fast and accurate document extraction with advanced vision capabilities.</p>
        </div>
        <div className="v1-info-card">
          <h4>About VJA</h4>
          <p>Uses Alibaba's Qwen VL model for multilingual document understanding with strong OCR and layout analysis.</p>
        </div>
        {isAdminOrSuperadmin && (
          <div className="v1-info-card">
            <h4>About XOrbit V3</h4>
            <p>Enhanced pipeline with image preprocessing (underline/tick removal) before Qwen VL extraction for improved accuracy.</p>
          </div>
        )}
        <div className="v1-info-card">
          <h4>Model Comparison</h4>
          <p>Upload a document once and extract with both models to compare accuracy, speed, and field-level differences.</p>
        </div>
      </div>
    </div>
  );
};

export default V1Dashboard;
